import axios from 'axios';
import { getUserToken } from './utility/localStorageFunctions';

const BASE_URL = `${process.env.REACT_APP_BASE_API_URL}/locationServices-api`

export interface OnwardLocationRequest {
  clientId: number
  leadPassengerId?: number
  flightNumber: string
  pickupDateTime: string
}

export type OnwardSuggestionSource = 'history' | 'team' | 'popular'

export interface OnwardDestination {
  addressLine1?: string
  addressLine2?: string
  town?: string
  postCode?: string
  country?: string
  countryCode?: string
  latitude: number
  longitude: number
  timeZone?: string
  source?: OnwardSuggestionSource
}

export interface OnwardLocationsResponse {
  passengerSuggestions?: OnwardDestination[]
  clientSuggestions?: OnwardDestination[]
  defaultSuggestions?: OnwardDestination[]
}

async function retrieveOnwardLocations(
  request: OnwardLocationRequest,
): Promise<OnwardLocationsResponse> {
  const token = await getUserToken('locationServices')

  const response = await axios({
    method: 'post',
    url: `${BASE_URL}/onward/retrieveOnwardLocations`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: {
      data: {
        clientId: request.clientId,
        leadPassengerId: request.leadPassengerId,
        flightNumber: request.flightNumber,
        pickupDateTime: request.pickupDateTime,
      },
    },
  })

  return response.data ?? {}
}

const destinationKey = (d: OnwardDestination): string => {
  const lat = Number.isFinite(d.latitude) ? d.latitude.toFixed(5) : ''
  const lng = Number.isFinite(d.longitude) ? d.longitude.toFixed(5) : ''
  if (lat && lng) return `${lat},${lng}`
  return `${(d.addressLine1 || '').trim().toLowerCase()}|${(d.postCode || '')
    .trim()
    .toLowerCase()}`
}

export function pickSuggestions(
  response: OnwardLocationsResponse,
  max = 3,
): OnwardDestination[] {
  const seen = new Set<string>()
  const tag = (
    list: OnwardDestination[] | undefined,
    source: OnwardSuggestionSource,
  ): OnwardDestination[] => {
    if (!list?.length) return []
    const kept: OnwardDestination[] = []
    for (const item of list) {
      const key = destinationKey(item)
      if (seen.has(key)) continue
      seen.add(key)
      kept.push({ ...item, source })
    }
    return kept
  }

  const tiers: OnwardDestination[][] = [
    tag(response.passengerSuggestions, 'history'),
    tag(response.clientSuggestions, 'team'),
    tag(response.defaultSuggestions, 'popular'),
  ]

  const picked: OnwardDestination[] = []
  while (picked.length < max && tiers.some((t) => t.length > 0)) {
    for (const tier of tiers) {
      if (picked.length >= max) break
      const next = tier.shift()
      if (next) picked.push(next)
    }
  }
  return picked
}

export const onwardAPI = {
  retrieveOnwardLocations,
}
